import { en } from './en';

/**
 * Russian dictionary — must implement the exact same shape as `en`
 * (compile-time guarantee: a missing key in `ru` is a type error).
 */
export const ru: typeof en = {
  common: {
    cancel: 'Отмена',
    create: 'Создать',
    save: 'Сохранить',
    done: 'Готово',
    close: 'Закрыть',
    details: 'Подробнее',
    none: 'Нет',
    active: 'Активна',
    inactive: 'Неактивна',
    unassigned: 'Не назначено',
    noClient: 'Без клиента',
    notAssigned: 'Не назначен',
  },
  roles: {
    manufacturer: 'Производитель',
    integrator: 'Интегратор',
  },
  nav: {
    primary: 'Основная навигация',
    dashboard: 'Дашборд',
    inquiries: 'Лид',
    clients: 'Клиенты',
    integrators: 'Интеграторы',
    bookings: 'Брони',
    catalog: 'Каталог',
    orders: 'Заказы',
    settings: 'Настройки',
  },
  auth: {
    signIn: 'Войти',
    signOut: 'Выйти',
    subtitle: 'CRM Завода Роботов',
    email: 'Email',
    password: 'Пароль',
    networkError: 'Ошибка сети — сервер запущен?',
  },
  status: {
    order: {
      DRAFT: 'Черновик',
      CONFIRMED: 'Подтверждён',
      IN_PRODUCTION: 'В производстве',
      SHIPPED: 'Отгружен',
      SOLD: 'Продан',
      CANCELLED: 'Отменён',
    },
  },
  stage: {
    validation: 'Входящие запросы',
    verification: 'Верификация',
    prioritization: 'Опция возможностей',
    realization: 'Концепт проекта',
    integratorSelection: 'Выбор интегратора',
    tkp: 'ТКП',
    // Issue #80 — новый шаг промышленного маршрута после ТКП.
    contract: 'Контракт',
    // Учебный робот: на доске этап концепта сразу включает ТКП,
    // а этап ТКП по сути уже контракт (слова заказчика, 13.10).
    realizationEducational: 'Концепт проекта / ТКП',
    tkpEducational: 'Контракт',
    won: 'Выиграно',
    lost: 'Отбой',
    paused: 'Пауза',
  },
  // Issue #53 — calendar view («Календарь»)
  calendar: {
    prevMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
    today: 'Сегодня',
    noDate: 'Без даты: {count}',
  },
  // Issue #52 — activity grouping («По делам») columns
  activity: {
    overdue: 'Просрочено',
    today: 'Сегодня',
    thisWeek: 'Эта неделя',
    nextWeek: 'Следующая неделя',
    noDate: 'Без даты',
    later: 'Больше двух недель',
  },
  confidence: {
    k90: 'К90',
    k50: 'К50',
    k10: 'К10',
    unset: 'Не задана',
    k90Label: 'Уверенные / Приоритетные',
    k50Label: 'Базовые',
    k10Label: 'Резервные / Рискованные',
    reasonPrompt: 'Почему такая оценка уверенности?',
  },
  inquiryType: {
    question: 'Запрос информации (вопрос)',
    request: 'Направлено ТЗ',
    invitation: 'Приглашение',
    partnership: 'Партнёрство (совместная реализация заказа)',
    saleOffer: 'Продажа (нам предлагают)',
    gisp: 'Запрос ГИСП',
    auditRequest: 'Запрос на Аудит',
    solutionHelp: 'Запрос на помощь в подборе решения',
  },
  transitions: {
    advance: 'Дальше',
    pause: 'Пауза',
    resume: 'Возобновить',
    // Issue #72 — the drawer action button says «Отказ»; the board status /
    // kanban column / timeline wording stays «Отбой» (stage.lost, typeDrop).
    drop: 'Отказ',
    win: 'Выиграно',
    setConfidence: 'Задать уверенность',
    reasonPrompt: 'Причина',
    reasonRequired: 'Для этого действия нужна причина.',
    confidenceRequiredAdvance: 'Чтобы перевести на стадию «{stage}», нужна оценка уверенности.',
    chooseConfidence: 'Оценка уверенности',
    confidencePlaceholder: 'Выберите…',
    interactionModeLabel: 'Способ работы',
    interactionModePlaceholder: 'Выберите…',
    modeAudit: 'Аудит возможности',
    modeRemoteTz: 'Формирование предложения по ТЗ',
    auditDateLabel: 'Дата аудита',
    interactionModeRequired: 'Выберите способ работы для перехода.',
    conceptLabel: 'Описание концепта',
    conceptPlaceholder: 'Что предложено клиенту: решение, объём, что обсуждается дальше.',
    conceptRequired: 'Опишите концепт или прикрепите артефакт вида «Концепт».',
    conceptArtifactHint: 'Есть прикреплённый концепт — текст можно не заполнять.',
    conceptApprovedLabel: 'Концепт согласован клиентом',
    conceptNotApproved: 'Пока не согласовано',
    productTypeLabel: 'Тип продукта',
    productTypePlaceholder: 'Выберите…',
    productTypeIntegration: 'Промышленный Робот',
    productTypeEducational: 'Учебный робот',
    productTypeRequired: 'Выберите тип продукта для перехода.',
    forkQuestion: 'Требуется дополнительная проработка концепта перед подготовкой ТКП?',
    forkTitle: 'Проработка концепта',
    forkYesConcept: 'Да, перейти к концепту',
    forkNoTkp: 'Нет, перейти к ТКП',
    conceptTitle: 'Концепт',
    confidenceAvailableFrom: 'Доступно с этапа «Опция возможностей»',
    reasonPlaceholder: 'Почему? Свободный текст.',
    remindAtLabel: 'Напомнить о сделке',
    remindAtHint: 'В этот день сделка автоматически вернётся в Валидацию.',
    reasonOptional: 'Причина (необязательно)',
    submit: 'Применить',
    advanceTo: 'Перевести на «{stage}»',
    advanceForward: 'Перевести дальше',
    nextStageHint: 'Следующий этап: {stage}',
    advanceBlockedNoManager: 'Сначала возьмите заявку в работу — назначьте ответственного менеджера',
    advanceBlockedNoClient: 'Сначала укажите клиента',
    changeConfidence: 'Изменить уверенность',
    setConfidenceShort: 'Задать',
    rollback: 'Откатить на шаг назад',
    rollbackSubmit: 'Откатить',
    rollbackHint: 'Назад на предыдущую стадию: {stage}',
    rollbackConfirm: 'Сделка вернётся на предыдущую стадию: «{stage}». Карточка и история остаются без изменений.',
    rollbackConfirmTerminal: 'Сделка вернётся в воронку на стадию «{stage}». Исход будет снят.',
    rollbackFunnelReturn: 'Возврат в воронку: {stage}',
    typeCreated: 'Создано',
    typeAdvance: 'Переведено дальше',
    typeSetStage: 'Стадия изменена',
    typeDrop: 'Отбой',
    typePause: 'Поставлено на паузу',
    typeResume: 'Возобновлено',
    typeWin: 'Выиграно',
    typeSetConfidence: 'Уверенность задана',
    typeAssignManager: 'Менеджер назначен',
    typeAssignClient: 'Клиент назначен',
    typeAttach: 'Файл прикреплён',
    typeComment: 'Комментарий',
    typeSetCriterion: 'Критерий оценён',
    typeRollback: 'Откат на шаг назад',
  },
  criteria: {
    title: 'Критерии верификации',
    average: 'Средняя',
    filled: 'заполнено {filled} из {total}',
    basketGreen: 'зелёная',
    basketYellow: 'жёлтая',
    basketRed: 'красная',
    empty: 'Оценок пока нет — проставьте критерии, чтобы получить предложение корзины.',
    noteLabel: 'Заметка',
    notePlaceholder: 'Почему такая оценка? (необязательно)',
    saveNote: 'Сохранить заметку',
    scoreOption: '{score} из 5',
    toastSaved: 'Оценка сохранена',
    toastFailed: 'Не удалось сохранить оценку',
    open: 'Критерии',
    openEmpty: 'Заполнить критерии',
    readOnlyStage: 'Критерии заполняются на стадии «Верификация».',
    historyLine: 'Критерий «{label}»: {score}',
    confidenceHint: 'по критериям: {basket}',
  },
  emailTemplates: {
    to: 'Кому',
    noAddress: 'адрес не найден',
    bodyLabel: 'Текст письма',
    helper: 'CRM не отправляет это письмо — менеджер отправляет его сам.',
    copy: 'Скопировать письмо',
    copied: 'Скопировано',
    pause:
      'Здравствуйте!\n\nСпасибо за обращение «{summary}». Мы вынуждены поставить его на паузу: {reason}.\n\nВернёмся к вам, как только появятся новости.\n\nС уважением,\nкоманда «Завод Роботов»',
    drop:
      'Здравствуйте!\n\nСпасибо за обращение «{summary}». К сожалению, сейчас мы не можем взять его в работу: {reason}.\n\nЕсли ситуация изменится, будем рады вернуться к разговору.\n\nС уважением,\nкоманда «Завод Роботов»',
  },
  attachments: {
    title: 'Артефакты',
    kindLabel: 'Вид',
    kindConcept: 'Концепт',
    kindTkp: 'ТКП',
    kindReport: 'Отчёт',
    kindOther: 'Другое',
    fileLabel: 'Файл',
    chooseFile: 'Выбрать файл',
    noFileChosen: 'Файл не выбран',
    titleLabel: 'Название',
    titlePlaceholder: 'Например: Концепт ячейки v1',
    descriptionLabel: 'Описание',
    descriptionPlaceholder: 'Пара слов о содержимом (необязательно)',
    upload: 'Загрузить',
    uploadArtifact: 'Загрузить артефакт',
    modalTitle: 'Загрузить артефакт',
    modalDesc: 'Файл появится в списке артефактов и в истории обращения.',
    download: 'Скачать',
    empty: 'Пока нет артефактов.',
    dropHint: 'Перетащите файл сюда или нажмите «Загрузить артефакт».',
    tooBig: 'Файл больше 15 МБ.',
    toastUploaded: 'Файл загружен',
    toastFailed: 'Загрузка не удалась',
    edit: 'Редактировать',
    editTitle: 'Редактировать артефакт',
    delete: 'Удалить',
    deleteTitle: 'Удалить артефакт',
    deleteConfirm: 'Удалить «{name}»? Действие необратимо.',
    toastUpdated: 'Артефакт обновлён',
    toastUpdateFailed: 'Не удалось сохранить изменения',
    toastDeleted: 'Артефакт удалён',
    toastDeleteFailed: 'Не удалось удалить артефакт',
  },
  dealParams: {
    // Issue #80 — блок «Параметры сделки» на шаге «Контракт».
    title: 'Параметры сделки',
    notFilled: 'Пока не заполнено',
    // П.2 — плашка «Скидка / Полная стоимость»: серая, пока не выбран режим.
    plateIdle: 'Скидка / Полная стоимость',
      modeDiscount: 'Скидка',
    modeFullPrice: 'Полная стоимость',
    plateChange: 'Изменить отображение',
    // П.1 — строки позиций.
    positionsTitle: 'Позиции',
    positionRobotType: 'Тип Робота',
      positionQuantity: 'Кол-во',
      addPosition: 'Добавить позицию',
    removePosition: 'Удалить позицию {row}',
    // П.3 — «Стоимость контракта».
    costTitle: 'Стоимость контракта',
    totalLabel: 'Итоговая стоимость контракта',
    totalLabelSimple: 'Общая стоимость контракта',
    positionsCountLabel: 'Кол-во продаваемых позиций',
    positionsCountValue: '{count} шт.',
    deliveryDateLabel: 'Дата поставки',
    robotLabel: 'Робот',
    robotHint: 'Суммарная стоимость по позициям «Робот» из контракта',
    rtcLabel: 'РТК',
    rtcHint: 'Итоговая стоимость по интеграции РТК из контракта — не включает «Роботов»',
    otherLabel: 'Стоимость иных услуг',
    otherHint: 'По необходимости — сопутствующие услуги вне РТК',
    paymentsTitle: 'Аванс / Оплата',
    paymentAdvanceLabel: 'Аванс',
    paymentPaymentLabel: 'Оплата {row}',
    paymentAmount: 'Сумма',
    paymentDate: 'Дата',
    addPayment: 'Добавить оплату',
    removePayment: 'Удалить строку оплаты {row}',
    // П.3 проверка (4.1) — живая подсказка; сервер блокирует только при WIN.
    sumOk: 'Разбивка сходится с итоговой стоимостью контракта',
    sumMismatch:
      'Не сходится: части дают {sum}, итог контракта — {total} (разница {diff}). С таким расхождением сделку не выиграть.',
    // П.4–6 — даты.
    shipmentsTitle: 'Дата отгрузки',
    shipmentPosition: 'Отгружаемая позиция',
    shipmentPositionPlaceholder: 'Выберите позицию или введите вручную',
    shipmentDate: 'Дата отгрузки',
    addShipment: 'Добавить строку отгрузки',
    removeShipment: 'Удалить строку отгрузки {row}',
    integrationTitle: 'Дата интеграции РТК',
    integrationNotBlocking: 'Не обязательно — сделку не блокирует.',
    addIntegrationDate: 'Добавить дату интеграции',
    removeIntegrationDate: 'Удалить строку даты интеграции {row}',
    closingLabel: 'Дата закрытия сделки',
    closingNotBlocking: 'Не обязательно — сделку не блокирует.',
    // Предпроверка черновика (клиентская; сервер перепроверяет).
    errPositionType: 'Укажите тип робота в каждой строке позиций.',
    errPositionQuantity: 'Количество — целое число от 1 и больше.',
    errPaymentAmount: 'Укажите сумму в каждой строке оплаты.',
    errShipmentLabel: 'Укажите позицию в каждой строке отгрузки.',
    errIntegrationDate: 'Укажите дату в каждой строке интеграции (или удалите строку).',
  },
  comments: {
    addTitle: 'Добавить запись',
    fieldTitle: 'Заголовок',
    titlePlaceholder: 'Коротко: о чём запись',
    fieldText: 'Текст',
    textPlaceholder: 'Подробности: договорённости, статус, следующий шаг…',
    submit: 'Добавить',
    toastAdded: 'Запись добавлена',
    toastFailed: 'Не удалось добавить запись',
  },
  pipeline: {
    subtitle: 'Отчётность коммерческого блока',
    scopeOwnBadge: 'Личные показатели — только ваши сделки',
    periodLast7Days: 'Последние 7 дней',
    periodWeek: 'Неделя',
    periodMonth: 'Месяц',
    periodQuarter: 'Квартал',
    periodFrom: 'С',
    periodTo: 'По',
    periodApply: 'Применить',
    periodRangeError: '«С» не может быть позже «По» — проверьте даты.',
    kpiIn: 'Пришло',
    kpiAdvanced: 'Готово',
    kpiDropped: 'Отвалилось',
    kpiPaused: 'Сейчас на паузе',
    kpiConversion: 'Конверсия',
    kpiConversionFunnel: 'Конверсия воронки',
    kpiConversionFunnelHint:
      'Произведение конверсий между стадиями 1–4 (по данным отчёта). Пара «Реализация» исключена: её выход — отдельное событие «победа».',
    kpiPortfolio: 'Портфель',
    funnelTitle: 'Конвейер поставки ценности',
    funnelNote: 'движение по стадиям',
    stageIn: 'Вошло',
    stageOut: 'Готово',
    stageAdvanced: 'прошло дальше',
    stageDropped: 'отвалилось',
    stagePaused: 'на паузе',
    inWork: 'в работе',
    conversionLabel: 'конверсия',
    funnelFootnote:
      'Счётчики: движение за выбранный период. «В работе» и суммы стадий: снимок на конец периода. Суммы по стадиям: оценка портфеля заказов в работе. Победы считаются отдельно: «прошло дальше» на стадии «Реализация» всегда 0 по дизайну.',
    portfolioTitle: 'Портфель проектов',
    baseConversionLabel: 'От входящих:',
    portfolioNote: 'приоритизация портфеля · открытые сделки всех стадий',
    portfolioBalance: 'Баланс портфеля:',
    dealsCount: 'Сделок: {count}',
    basketShareHint: '{share}% суммы портфеля',
    basketOpen: 'открыть',
    millionShort: 'млн ₽',
    drillInTitle: 'Сделки группы',
    drillInScopeNote: 'Сделки, зашедшие на стадии 3–4 за выбранный период',
    drillInOfCount: '{shown} из {total}',
    drillInEmpty: 'Нет сделок в этой группе за период.',
    drillInNoAmount: '—',
    attentionTitle: 'Требует внимания',
    attentionNoConfidence: 'Не задана уверенность',
    attentionStale: 'Нет движения {days} дн',
    attentionOverdueTasks: 'Просроченные задачи',
    attentionAllGood: 'За выбранный период всё в порядке.',
    attentionNoConfidenceCount:
      '{count} сделок на стадиях «Приоритизация»/«Реализация» без уверенности — задайте корзины, чтобы отчёт был честным.',
    attentionPausedCount: '{count} сделок сейчас на паузе — проверьте сроки возобновления.',
    attentionDropSpike:
      'На стадии «{stage}» за период отвалилось больше ({dropped}), чем прошло дальше ({advanced}).',
    noAccessTitle: 'Отчётность — только для производителя',
    noAccessDesc:
      'Отчёт коммерческого блока доступен роли производителя. Текущие цифры уточните у контакта производителя.',
    errorTitle: 'Не удалось загрузить отчёт',
    retry: 'Повторить',
    emptyStage: 'Нет сделок',
    funnelClickHint: 'клик по карточке стадии — подробная аналитика',
    attentionNote: 'автоподсказки по данным отчёта',
    sdSummary: 'Сводка',
    sdCameIn: 'Вошло',
    sdWentOut: 'Готово',
    sdOutSplit: 'дальше {advanced} · отбой {dropped} · пауза {paused} · назад {returned}',
    sdCurrent: 'Сейчас в стадии',
    sdCurrentSum: 'Портфель стадии',
    sdConversion: 'Конверсия дальше',
    sdAvgAge: 'Средний возраст, дн.',
    sdFromTitle: 'Откуда пришли',
    sdToTitle: 'Куда ушли',
    sdFromNew: 'Новые обращения',
    sdFromPause: 'С паузы',
    sdToDropped: 'Отбой',
    sdToPaused: 'Пауза',
    sdToWon: 'Выиграно',
    sdEmpty: 'За период движения нет.',
    sdTabCurrent: 'Сейчас в стадии',
    sdTabIn: 'Вошло',
    sdTabOut: 'Готово',
    sdTabDropped: 'Отбой',
    sdTabPaused: 'Пауза',
    sdAgeDays: '{days} д',
    sdManagerUnset: '—',
  },
  dashboard: {
    title: 'Дашборд',
    subtitleAll: 'Обзор воронки — все обращения.',
    subtitleAssigned: 'Обзор воронки — назначенные вам обращения.',
    kanbanTitle: 'Канбан воронки появится в Фазе 4',
    kanbanDescription:
      'Воронка обращений (NEW → QUALIFYING → QUOTING → BOOKED → WON / REJECTED) будет жить здесь. Фаза 2 поставляет UI-систему, на которой построен этот экран.',
    openApiDocs: 'Открыть API-документацию',
  },
  placeholder: {
    arrives: 'Раздел «{title}» появится в Фазе 4',
    description:
      'Фаза 2 поставляет UI-систему; экраны этого раздела появятся в сборке Фазы 4.',
  },
  settings: {
    title: 'Настройки',
    description: 'Настройки рабочего пространства.',
    languageSection: 'Язык интерфейса',
    languageLabel: 'Язык',
    languageHelper: 'Применяется сразу и сохраняется для вашего пользователя на этом устройстве.',
    russian: 'Русский',
    english: 'English',
  },
  inquiries: {
    title: 'Лид',
    descAll: 'Воронка продаж — все источники, все интеграторы.',
    descOwn: 'Переданные вам обращения.',
    new: 'Новый Лид',
    emptyTitle: 'Обращений пока нет',
    emptyDesc: 'Создайте первое обращение, чтобы запустить воронку.',
    modalDesc: 'Зафиксируйте возможность продажи. Клиента и интегратора можно назначить позже.',
    source: 'Источник',
    sourceEndCustomer: 'Конечный заказчик',
    sourceIntegrator: 'Интегратор',
    sourcePartnerChkpz: 'Партнёр — ЧКПЗ',
    sourceEmail: 'Сотрудник ЗР',
    channel: 'Канал',
    channelPlaceholder: 'например, выставка, LinkedIn, рекомендация RKM Engineering',
    channelHelper: 'Произвольный контекст — откуда пришло обращение.',
    client: 'Клиент',
    assignClient: 'Указать клиента',
    assignClientHelper: 'Не указан — найдите существующего клиента или создайте нового.',
    unassignedOption: '— Не назначен —',
    clientHelper: 'Необязательно — привяжите к известному клиенту сейчас или позже.',
    clientSearchPlaceholder: 'Начните вводить название компании…',
    clientNoResults: 'Клиенты не найдены.',
    clientCreateNew: 'Создать клиента «{name}»',
    clientClear: 'Снять выбор клиента',
    clientNewDesc: 'Новый клиент будет создан и привязан к обращению.',
    innPlaceholder: 'например, 7706123456',
    innHelper: 'По ИНН найдём сохранённого клиента или создадим нового.',
    summary: 'Суть обращения',
    summaryPlaceholder: 'Краткое описание того, что нужно заказчику.',
    // Issue #63 — «Название карточки» (необязательно; пусто — название клиента)
    cardTitle: 'Название карточки',
    cardTitlePlaceholder: 'например, Линия паллетирования — Северсталь',
    cardTitleHelper: 'Необязательно. Если пусто — показывается название клиента.',
    assignIntegrator: 'Назначить Интегратора на расчёт ТКП',
    // Issue #78 — «Генподрядчик» block (concept step on)
    generalContractorTitle: 'Генподрядчик',
      generalContractorAuto: 'Заполняется автоматически',
    generalContractorPlaceholder: 'Название компании',
    generalContractorPick: 'Выбрать из интеграторов',
    tkpArtifactHint: 'ТКП загружен как артефакт',
    assignHelper: 'Можно выбрать несколько интеграторов сразу. Отправка запроса — отдельный этап.',
    tkpIntegratorsPlaceholder: 'Выберите интеграторов…',
    tkpIntegratorsChosen: 'Выбрано: {count}',
    tkpIntegratorsEmpty: 'Активных интеграторов нет.',
    changeStatus: 'Изменить статус обращения',
    age: '{age} назад',
    toastMoved: 'Обращение перемещено',
    toastCreated: 'Обращение создано',
    toastUpdateFail: 'Не удалось обновить обращение',
    toastCreateFail: 'Не удалось создать обращение',
    errorUpdate: 'Не удалось обновить обращение',
    errorCreate: 'Не удалось создать обращение',
    // Issue #63 — редактирование созданной заявки
    edit: 'Редактировать',
    editTitle: 'Редактирование обращения',
    amountPlaceholder: 'например, 12500000',
    amountInvalid: 'Введите сумму числом — например, 12500000.',
    toastUpdated: 'Изменения сохранены',
    toastEditFail: 'Не удалось сохранить изменения',
    errorEdit: 'Не удалось сохранить изменения',
    viewLabel: 'Вид',
    viewBoard: 'Канбан',
    viewList: 'Список',
    viewActivity: 'По делам',
    viewCalendar: 'Календарь',
    overdueCount: 'Просрочено дел: {count}',
    fieldNextStep: 'Дата следующего шага',
    nextStepClear: 'Очистить дату следующего шага',
    searchAria: 'Поиск обращений',
    searchPlaceholder: 'Клиент, RF-id, название карточки…',
    filterStage: 'Стадия',
    filterConfidence: 'Уверенность',
    filterIntegrator: 'Интегратор',
    filterSource: 'Источник',
    filterType: 'Тип',
    filterManager: 'Менеджер',
    typeLabel: 'Тип запроса',
    filterOutcome: 'Исход',
    allOption: 'Все',
    pausedCount: '{count}',
    pausedListLabel: 'Приостановленные сделки этой стадии',
    colClient: 'Клиент',
    colSource: 'Источник',
    colType: 'Тип',
    colSummary: 'Суть',
    colStatus: 'Статус',
    colIntegrator: 'Интегратор',
    colManager: 'Менеджер',
    colAmount: 'Сумма',
    colCreated: 'Создано',
    colAge: 'Возраст',
    sortNone: 'Без сортировки',
    sortAsc: 'По возрастанию',
    sortDesc: 'По убыванию',
    closedLabel: 'закрыто',
    stageProgress: 'Этап {current} из {total}',
    emptyFilteredTitle: 'По фильтрам ничего не найдено',
    emptyFilteredDesc: 'Попробуйте очистить поиск или фильтры.',
    timelineTitle: 'История',
    timelineEmpty: 'Пока нет событий.',
    drawerDetailsTitle: 'Сделка',
    drawerParticipantsTitle: 'Участники',
    drawerActionsTitle: 'Статус',
    fieldType: 'Тип',
    fieldConfidence: 'Уверенность',
    fieldAmount: 'Сумма',
    fieldIntegrator: 'Интегратор',
    fieldCreatedBy: 'Создал',
    fieldCreated: 'Создано',
    fieldClientEmail: 'Email клиента',
    fieldSourceEmail: 'Почта клиента',
    clientEmail: 'Почта клиента',
    clientEmailPlaceholder: 'name@company.ru',
    clientEmailInvalid: 'Проверьте формат почты — например, name@company.ru.',
    confidenceReasonLabel: 'Обоснование уверенности',
    pauseReasonLabel: 'Причина паузы',
    pauseRemindLabel: 'Напоминание',
    remindUntil: 'до {date}',
    fromPauseBadge: 'Из заморозки',
    outcomeReasonLabel: 'Причина исхода',
    toastTransition: 'Обращение обновлено',
    toastTransitionFail: 'Действие не удалось',
    dndErrRole: 'Перенос сделок доступен только производителю.',
    dndErrBackward: 'Перенос назад по стадиям не поддерживается.',
    dndErrSkip: 'Можно перенести только на одну стадию вперёд.',
    dndErrOffRoute: 'Эта стадия не входит в маршрут этого типа продукта.',
    dndErrWinStage: 'Выиграть можно только сделку на стадиях «Концепт проекта» и «ТКП».',
    listFoot: 'Показано {shown} из {total}',
    boardFootTotal: 'Всего в воронке: {count}',
    boardFootAmount: 'Активная работа: {amount}',
    openDetails: 'Открыть детали обращения',
    fieldManager: 'Менеджер',
    takeIntoWork: 'Взять в работу',
    taken: 'В работе',
    notTaken: 'Не взято',
    toastTaken: 'Обращение взято в работу',
    toastTakeFail: 'Не удалось взять в работу',
    warnNoManager: 'Менеджер не назначен — обращение ещё не взято в работу. Всё равно перевести дальше?',
  },
  clients: {
    title: 'Клиенты',
    descAll: 'Конечные заказчики в воронке — все клиенты всех интеграторов.',
    descOwn: 'Клиенты, забронированные за вашим интегратором.',
    add: 'Добавить клиента',
    emptyTitle: 'Клиентов пока нет',
    emptyDescManufacturer:
      'Создайте первую карточку клиента, чтобы начать отслеживать обращения и брони.',
    emptyDescIntegrator:
      'Клиенты появятся здесь, когда производитель забронирует их за вашим интегратором.',
    colCompany: 'Компания',
    colInn: 'ИНН',
    colContact: 'Контакт',
    colEmail: 'Email',
    colPhone: 'Телефон',
    colIndustry: 'Отрасль',
    colBooking: 'Бронь',
    modalTitle: 'Добавить клиента',
    modalDesc: 'Создать новую карточку конечного заказчика.',
    fieldCompanyName: 'Название компании',
    fieldInn: 'ИНН',
    fieldWebsite: 'Сайт',
    fieldContactName: 'Контактное лицо',
    fieldPosition: 'Должность',
    fieldContactEmail: 'Email контакта',
    fieldContactPhone: 'Телефон контакта',
    fieldIndustry: 'Отрасль',
    fieldNotes: 'Заметки',
    bookingHistory: 'История броней',
    noBookings: 'Броней пока нет.',
    colIntegrator: 'Интегратор',
    colStatus: 'Статус',
    colDate: 'Дата',
    toastCreated: 'Клиент создан',
    toastUpdated: 'Клиент обновлён',
    toastUpdateFail: 'Не удалось обновить клиента',
    errorUpdate: 'Не удалось обновить клиента',
    edit: 'Редактировать',
    editModalTitle: 'Редактировать клиента',
    editModalDesc: 'Изменить карточку конечного заказчика.',
    toastCreateFail: 'Не удалось создать клиента',
    errorCreate: 'Не удалось создать клиента',
  },
  contacts: {
    title: 'Контакты',
    add: 'Добавить контакт',
    empty: 'Контактов пока нет.',
    primary: 'Основной',
    makePrimary: 'Сделать основным',
    edit: 'Редактировать контакт',
    delete: 'Удалить контакт',
    deleteConfirm: 'Удалить этот контакт?',
    fieldLastName: 'Фамилия',
    fieldFirstName: 'Имя',
    fieldMiddleName: 'Отчество',
    fieldPhone: 'Телефон',
    fieldEmail: 'Email',
    fieldRole: 'Роль',
    fieldPositionNote: 'Должность / примечание',
    modalAddTitle: 'Добавить контакт',
    modalEditTitle: 'Редактировать контакт',
    toastCreated: 'Контакт добавлен',
    toastUpdated: 'Контакт обновлён',
    toastDeleted: 'Контакт удалён',
    toastFail: 'Не удалось сохранить контакт',
    roles: {
      SECRETARY: 'Секретарь',
      CEO: 'Генеральный директор',
      PRODUCTION_DIRECTOR: 'Директор по производству',
      CHIEF_TECHNOLOGIST: 'Главный технолог',
      ENGINEER: 'Инженер',
      OTHER: 'Другое',
    },
  },
  orders: {
    title: 'Заказы',
    descAll: 'Производственный конвейер — все интеграторы.',
    descOwn: 'Ваши заказы.',
    new: 'Новый заказ',
    searchAria: 'Поиск заказов',
    searchPlaceholder: 'Поиск по клиенту, модели или SKU…',
    emptySearchTitle: 'По запросу заказы не найдены',
    emptySearchDesc: 'Попробуйте другой запрос.',
    emptyTitle: 'Заказов пока нет',
    emptyDescManufacturer: 'Создайте первый заказ, чтобы запустить производственный конвейер.',
    emptyDescIntegrator: 'Создайте заказ для одного из забронированных клиентов.',
    colClient: 'Клиент',
    colIntegrator: 'Интегратор',
    colModel: 'Модель',
    colQty: 'Кол-во',
    colUnitPrice: 'Цена за шт.',
    colTotal: 'Сумма',
    colStatus: 'Статус',
    colCreated: 'Создан',
    modalTitle: 'Новый заказ',
    modalDesc: 'Создать заказ для забронированного клиента.',
    integratorLabel: 'Интегратор',
    selectIntegrator: '— Выберите интегратора —',
    onlyActiveIntegrators: 'Только активные интеграторы.',
    clientLabel: 'Клиент',
    selectIntegratorFirst: '— Сначала выберите интегратора —',
    selectClient: '— Выберите клиента —',
    clientHelperManufacturer: 'Только клиенты, забронированные за выбранным интегратором.',
    clientHelperIntegrator: 'Только клиенты, забронированные за вашим интегратором.',
    modelLabel: 'Модель робота',
    selectModel: '— Выберите модель —',
    modelHelper: 'При выборе модели цена подставится автоматически; её можно изменить.',
    quantity: 'Количество',
    unitPriceUsd: 'Цена за шт. (USD)',
    orderTotal: 'Сумма заказа',
    changeStatus: 'Изменить статус заказа',
    clientFallback: 'Клиент',
    orderFallback: 'Заказ',
    toastCreated: 'Заказ создан',
    toastStatusUpdated: 'Статус обновлён',
    toastCreateFail: 'Не удалось создать заказ',
    toastStatusFail: 'Не удалось обновить статус',
    errorCreate: 'Не удалось создать заказ',
    errorStatus: 'Не удалось обновить статус',
  },
  bookings: {
    title: 'Брони',
    descManufacturer: 'Назначения клиент ↔ интегратор. Создание и снятие — только производитель.',
    descIntegrator: 'Клиенты, забронированные за вашим интегратором.',
    book: 'Забронировать клиента',
    searchAria: 'Поиск броней',
    searchPlaceholder: 'Поиск по клиенту или интегратору…',
    filterAria: 'Фильтр по активности',
    filterActive: 'Только активные',
    filterInactive: 'Только неактивные',
    filterAll: 'Все брони',
    emptySearchTitle: 'По запросу брони не найдены',
    emptySearchDesc: 'Попробуйте другой запрос или измените фильтр.',
    emptyTitle: 'Броней пока нет',
    emptyDescManufacturer: 'Забронируйте клиента за интегратором, чтобы начать отслеживать передачу.',
    emptyDescIntegrator: 'Брони за вашим интегратором появятся здесь.',
    colClient: 'Клиент',
    colIntegrator: 'Интегратор',
    colStatus: 'Статус',
    colBookedAt: 'Забронировано',
    statusReleased: 'Снята',
    release: 'Снять',
    modalTitle: 'Забронировать клиента за интегратором',
    modalDesc:
      'Назначьте клиента интегратору. Интегратор увидит этого клиента и сможет создавать для него заказы.',
    bookSubmit: 'Забронировать',
    clientLabel: 'Клиент',
    selectClient: '— Выберите клиента —',
    integratorLabel: 'Интегратор',
    selectIntegrator: '— Выберите интегратора —',
    onlyActiveIntegrators: 'Показаны только активные интеграторы.',
    clientFallback: 'Клиент',
    integratorFallback: 'Интегратор',
    toastCreated: 'Бронь создана',
    toastReleased: 'Бронь снята',
    toastCreateFail: 'Не удалось создать бронь',
    toastReleaseFail: 'Не удалось снять бронь',
    errorCreate: 'Не удалось создать бронь',
    errorRelease: 'Не удалось снять бронь',
  },
  catalog: {
    title: 'Каталог роботов',
    descManufacturer: 'Доступные модели роботов и базовые цены.',
    descIntegrator: 'Доступные модели роботов — используются при создании заказов.',
    add: 'Добавить модель',
    emptyTitle: 'Моделей роботов пока нет',
    emptyDescManufacturer: 'Добавьте первый SKU, чтобы начать продавать.',
    emptyDescIntegrator: 'Производитель пока не добавил ни одной модели.',
    colSku: 'SKU',
    colName: 'Название',
    colDescription: 'Описание',
    colBasePrice: 'Базовая цена',
    colStatus: 'Статус',
    statusDiscontinued: 'Снята с производства',
    modalTitle: 'Добавить модель робота',
    modalDesc: 'Определите новый SKU, доступный для заказов.',
    fieldSku: 'SKU',
    skuPlaceholder: 'например, RF-6AX-PRO',
    fieldName: 'Название',
    fieldBasePriceUsd: 'Базовая цена (USD)',
    fieldDescription: 'Описание',
    descriptionPlaceholder: 'Характеристики, целевые задачи, отличительные особенности.',
    activeLabel: 'Активна (доступна для новых заказов)',
    toastCreated: 'Модель робота создана',
    toastCreateFail: 'Не удалось создать модель',
    errorCreate: 'Не удалось создать модель',
  },
  integrators: {
    title: 'Интеграторы',
    description: 'Партнёры по интеграции роботов (RKM).',
    add: 'Добавить интегратора',
    notAvailableTitle: 'Недоступно',
    notAvailableDesc: 'Интеграторами управляет производитель.',
    emptyTitle: 'Интеграторов пока нет',
    emptyDesc: 'Создайте первого партнёра по интеграции, чтобы бронировать за ним клиентов.',
    colName: 'Название',
    colEmail: 'Email',
    colPhone: 'Телефон',
    colPriority: 'Приоритет',
    colSpecialization: 'Специализация',
    colStatus: 'Статус',
    modalTitle: 'Добавить интегратора',
    modalDesc: 'Зарегистрировать нового партнёра по интеграции.',
    fieldName: 'Название',
    fieldContactEmail: 'Email контакта',
    fieldContactPhone: 'Телефон контакта',
    fieldPriority: 'Приоритет',
    priorityHelper: 'Больше число — выше приоритет при бронировании.',
    fieldSpecialization: 'Специализация',
    specializationPlaceholder: 'например, пищевое производство, автопром, логистика',
    activeLabel: 'Активен (доступен для новых броней)',
    toastCreated: 'Интегратор создан',
    toastCreateFail: 'Не удалось создать интегратора',
    errorCreate: 'Не удалось создать интегратора',
  },
  notifications: {
    bell: 'Уведомления',
    title: 'Уведомления',
    empty: 'Пока нет уведомлений',
    markAllRead: 'Прочитать все',
    unread: 'Непрочитанных: {count}',
    unreadOne: 'Непрочитано',
    type: {
      tkpAttached: 'ТКП готов',
      managerAssigned: 'Назначено вам',
      stageAdvanced: 'Смена стадии',
    },
  },
  components: {
    modal: { close: 'Закрыть диалог' },
    drawer: { close: 'Закрыть панель' },
    toast: {
      dismiss: 'Закрыть уведомление',
      region: 'Уведомления',
    },
    entityLink: {
      openClient: 'Открыть карточку клиента {name}',
      openIntegrator: 'Открыть карточку интегратора {name}',
    },
  },
};
